import {
    createRoutesFromElements,
    createBrowserRouter,
    Navigate,
    Route,
    useLocation,
    useNavigate,
} from 'react-router-dom'


import { WEB_ROUTE_SEGMENTS } from './constants'
import { shouldUseDirectCheckoutStart } from './checkout-flow'
import {
    getCheckoutFallbackPath,
    getCheckoutFlow,
    getValidatedCheckoutReturnTo,
    getCheckoutPaymentGuardRedirectPath,
    hasCheckoutHistoryEntry,
    getCheckoutPath,
    getCheckoutPaymentPath,
    isAdditionalPaymentCheckoutFlow,
    normalizeCheckoutLocale,
} from './checkout-paths'
import { getStripeCheckoutUiModeForCurrentDevice } from './stripe-checkout'
import CheckoutDirectStartView from '@/views/checkout-direct-start'
import CheckoutView from '@/views/checkout'
import CheckoutPaymentView from '@/views/checkout-payment'
import CheckoutSuccessView from '@/views/checkout-success'
import ErrorView from './pwa/views/error'
import GaragesMapView from '@/views/garages-map'

function CheckoutRoute() {
    const navigate = useNavigate()
    const location = useLocation()
    const locale = normalizeCheckoutLocale(location.pathname.split('/')[1])
    const fallbackPath = getCheckoutFallbackPath(locale)

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1)
            return
        }

        const returnTo = getValidatedCheckoutReturnTo(location.search)

        if (returnTo) {
            window.location.assign(returnTo)
            return
        }

        window.location.assign(fallbackPath)
    }

    return (
        <CheckoutView
            onBack={handleBack}
            onPay={() => navigate(getCheckoutPaymentPath({ isPwa: false, locale, searchParams: location.search }))}
        />
    )
}

export function CheckoutPaymentRoute() {
    const navigate = useNavigate()
    const location = useLocation()
    const locale = normalizeCheckoutLocale(location.pathname.split('/')[1])
    const checkoutPath = getCheckoutPath({ isPwa: false, locale, searchParams: location.search })
    const returnTo = getValidatedCheckoutReturnTo(location.search)
    const checkoutFlow = getCheckoutFlow(location.search)
    const checkoutUiMode = getStripeCheckoutUiModeForCurrentDevice(false)
    const redirectPath = isAdditionalPaymentCheckoutFlow(checkoutFlow)
        ? null
        : getCheckoutPaymentGuardRedirectPath({
            isPwa: false,
            locale,
            searchParams: location.search,
            checkoutUiMode,
        })

    if (redirectPath) {
        return <Navigate to={redirectPath} replace />
    }

    const handleBack = () => {
        if (hasCheckoutHistoryEntry(window.history.state)) {
            navigate(-1)
            return
        }

        if (returnTo) {
            window.location.assign(returnTo)
            return
        }

        navigate(checkoutPath, { replace: true })
    }

    if (shouldUseDirectCheckoutStart(checkoutFlow, checkoutUiMode)) {
        return <CheckoutDirectStartView onBack={handleBack} />
    }

    return <CheckoutPaymentView onBack={handleBack} />
}

function CheckoutSuccessRoute() {
    return <CheckoutSuccessView />
}

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route errorElement={<ErrorView />}>
            <Route path="cs">
                <Route
                    path={WEB_ROUTE_SEGMENTS.checkout.cs}
                    element={<CheckoutRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.cs}/payment`}
                    element={<CheckoutPaymentRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.cs}/success`}
                    element={<CheckoutSuccessRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={WEB_ROUTE_SEGMENTS.garageMap.cs}
                    element={<GaragesMapView />}
                    errorElement={<ErrorView embedded />}
                />
            </Route>
            <Route path="en">
                <Route
                    path={WEB_ROUTE_SEGMENTS.checkout.en}
                    element={<CheckoutRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.en}/payment`}
                    element={<CheckoutPaymentRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.en}/success`}
                    element={<CheckoutSuccessRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={WEB_ROUTE_SEGMENTS.garageMap.en}
                    element={<GaragesMapView />}
                    errorElement={<ErrorView embedded />}
                />
            </Route>
            <Route path="de">
                <Route
                    path={WEB_ROUTE_SEGMENTS.checkout.de}
                    element={<CheckoutRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.de}/payment`}
                    element={<CheckoutPaymentRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.de}/success`}
                    element={<CheckoutSuccessRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={WEB_ROUTE_SEGMENTS.garageMap.de}
                    element={<GaragesMapView />}
                    errorElement={<ErrorView embedded />}
                />
            </Route>
            <Route path="pl">
                <Route
                    path={WEB_ROUTE_SEGMENTS.checkout.pl}
                    element={<CheckoutRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.pl}/payment`}
                    element={<CheckoutPaymentRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={`${WEB_ROUTE_SEGMENTS.checkout.pl}/success`}
                    element={<CheckoutSuccessRoute />}
                    errorElement={<ErrorView embedded />}
                />
                <Route
                    path={WEB_ROUTE_SEGMENTS.garageMap.pl}
                    element={<GaragesMapView />}
                    errorElement={<ErrorView embedded />}
                />
            </Route>
        </Route>
    )
)

export default router
